import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
  SimpleChanges,
  OnChanges,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormControl,
  FormArray,
  Validators,
  ReactiveFormsModule,
  AbstractControl,
} from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subject, takeUntil, filter, take } from 'rxjs';
import { InputComponent } from '../input/input.component';
import { SelectComponent } from '../select/select.component';
import { CheckboxComponent } from '../checkbox/checkbox.component';
import { RadioComponent } from '../radio/radio.component';
import { FileUploadComponent } from '../file-upload/file-upload.component';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import {
  DynamicFormConfig,
  DynamicFormValue,
  DynamicFormState,
  FormSection,
  FormField,
  DataSourceConfig,
} from './models/dynamic-form.model';
import { TextareaComponent } from '../text-area/text-area.component';
import { DatepickerComponent } from '../datepicker/datepicker.component';
import { TimepickerComponent } from '../timepicker/timepicker.component';
import { ColorpickerComponent } from '../colorpicker/colorpicker.component';
import { FieldGroupComponent } from './feild-group/field-group.component';
import { FieldDataService } from './feild-group/services/field-data.service';

@Component({
  selector: 'app-dynamic-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputComponent,
    SelectComponent,
    TextareaComponent,
    CheckboxComponent,
    RadioComponent,
    FileUploadComponent,
    DatepickerComponent,
    TimepickerComponent,
    ColorpickerComponent,
    ButtonsModule,
    FieldGroupComponent,
  ],
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicFormComponent implements OnInit, OnDestroy, OnChanges {
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fieldDataService = inject(FieldDataService);
  private readonly destroy$ = new Subject<void>();

  @ViewChildren(InputComponent) inputComponents!: QueryList<InputComponent>;
  @ViewChildren(SelectComponent) selectComponents!: QueryList<SelectComponent>;
  @ViewChildren(TextareaComponent) textareaComponents!: QueryList<TextareaComponent>;
  @ViewChildren(DatepickerComponent) datepickerComponents!: QueryList<DatepickerComponent>;
  @ViewChildren(TimepickerComponent) timepickerComponents!: QueryList<TimepickerComponent>;
  @ViewChildren(ColorpickerComponent) colorpickerComponents!: QueryList<ColorpickerComponent>;
  @ViewChildren(CheckboxComponent) checkboxComponents!: QueryList<CheckboxComponent>;
  @ViewChildren(RadioComponent) radioComponents!: QueryList<RadioComponent>;
  @ViewChildren(FileUploadComponent) fileUploadComponents!: QueryList<FileUploadComponent>;
  @ViewChildren(FieldGroupComponent) fieldGroupComponents!: QueryList<FieldGroupComponent>;

  @Input() config!: DynamicFormConfig;
  @Input() initialValues?: DynamicFormValue;
  @Input() submitOnEnter: boolean = false;
  @Input() disabled: boolean = false;
  @Input() mode?: 'popup' | 'page';

  @Output() formSubmit = new EventEmitter<DynamicFormValue>();
  @Output() formCancel = new EventEmitter<void>();
  @Output() formChange = new EventEmitter<DynamicFormState>();
  @Output() fieldChange = new EventEmitter<{ fieldId: string; value: any }>();
  @Output() formReady = new EventEmitter<FormGroup>();

  form!: FormGroup;
  visibleSections: FormSection[] = [];
  fieldVisibility: Map<string, boolean> = new Map();
  fieldDisabled: Map<string, boolean> = new Map();
  currentMode: 'popup' | 'page' = 'page';

  // Data source tracking
  private readonly fieldDataSourceConfigs = new Map<string, DataSourceConfig>();
  private previousFormValues: DynamicFormValue = {};
  private dataSourcesInitialized = false;

  ngOnInit(): void {
    this.currentMode = this.mode ?? this.config.mode ?? 'page';
    this.initializeForm();
    this.setupFormSubscriptions();
    this.updateVisibility();
    this.initializeDataSources();
    this.formReady.emit(this.form);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['config'] && !changes['config'].firstChange) {
      this.initializeForm();
      this.setupFormSubscriptions();
      this.updateVisibility();
      this.initializeDataSources();
    }
  }

  /**
   * Main entry point for form initialization
   */
  private initializeForm(): void {
    // Store data source configs for all fields
    this.storeDataSourceConfigs();

    const controls = this.buildFormControls();
    this.form = this.fb.group(controls);

    this.updateControlValidities();

    if (this.disabled) {
      this.form.disable({ emitEvent: false });
    }
  }

  /**
   * Store data source configurations for quick lookup
   */
  private storeDataSourceConfigs(): void {
    this.fieldDataSourceConfigs.clear();

    this.config.sections.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.dataSource) {
          this.fieldDataSourceConfigs.set(field.id, field.dataSource);
        }
      });
    });
  }

  /**
   * Initialize data sources for all fields
   */
  private initializeDataSources(): void {
    if (this.dataSourcesInitialized) return;

    const formValue = this.form.getRawValue();

    // First, load data for fields without dependencies
    this.config.sections.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.dataSource && (!field.dependsOn || field.dependsOn.length === 0)) {
          this.loadFieldData(field, formValue);
        }
      });
    });

    // Then, load data for fields with dependencies (if dependencies are satisfied)
    this.config.sections.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.dataSource && field.dependsOn && field.dependsOn.length > 0) {
          const allDependenciesMet = this.areDependenciesMet(field, formValue);

          if (allDependenciesMet) {
            this.loadFieldData(field, formValue);
          }
        }
      });
    });

    // Store initial values for change detection
    this.previousFormValues = { ...formValue };
    this.dataSourcesInitialized = true;
  }

  /**
   * Check if all dependencies for a field are met
   */
  private areDependenciesMet(field: FormField, formValue: DynamicFormValue): boolean {
    if (!field.dependsOn || field.dependsOn.length === 0) {
      return true;
    }

    return field.dependsOn.every((dep) => {
      const depValue = formValue[dep];
      return depValue !== null && depValue !== undefined && depValue !== '';
    });
  }

  /**
   * Load data for a field using its data source
   */
  private loadFieldData(field: FormField, formValue: DynamicFormValue): void {
    if (!field.dataSource) return;

    this.fieldDataService
      .loadData(field.id, field.dataSource, { formValue })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.updateFieldDataSource(field, data);
        },
        error: (error) => {
          console.error(`Error loading data for field ${field.id}:`, error);
        },
      });
  }

  /**
   * Update field's data source with new data
   */
  private updateFieldDataSource(field: FormField, data: any[]): void {
    if (field.config) {
      // Create a new config object to trigger change detection
      field.config = {
        ...field.config,
        data: data,
      };

      const control = this.form.get(field.formControlName);
      if (control) {
        control.updateValueAndValidity({ emitEvent: false });
      }
    }
    this.cdr.markForCheck();
  }

  /**
   * Handle dependent field changes - reload data when parent changes
   */
  private handleDependentFieldChanges(currentValue: DynamicFormValue): void {
    this.config.sections.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.dependsOn && field.dependsOn.length > 0 && field.dataSource) {
          // Check if any dependency value has changed
          const dependencyChanged = field.dependsOn.some((dep) => {
            const prevValue = this.previousFormValues[dep];
            const currValue = currentValue[dep];
            return prevValue !== currValue;
          });

          if (dependencyChanged) {
            // Clear the current field value when dependency changes
            const control = this.form.get(field.formControlName);
            if (control && field.dataSource.dependsOnFields) {
              control.setValue(null, { emitEvent: false });
            }

            // Call onDependencyChange if provided
            if (field.onDependencyChange) {
              field.onDependencyChange(currentValue, field.id);
            }

            // Check if dependencies are satisfied
            const allDependenciesMet = this.areDependenciesMet(field, currentValue);

            if (allDependenciesMet) {
              // Add a small delay to ensure the DOM updates
              setTimeout(() => {
                this.loadFieldData(field, currentValue);
              }, 0);
            } else {
              // Clear the field's data source if dependencies not met
              this.updateFieldDataSource(field, []);
            }
          }
        }
      });
    });

    // Store current values for next comparison
    this.previousFormValues = { ...currentValue };
  }

  /**
   * Build all form controls from configuration
   */
  private buildFormControls(): { [key: string]: FormControl | FormArray } {
    const controls: { [key: string]: FormControl | FormArray } = {};

    this.config.sections.forEach((section) => {
      section.fields.forEach((field) => {
        controls[field.formControlName] = this.createFormControl(field);
      });
    });

    return controls;
  }

  /**
   * Create a form control based on field type
   */
  private createFormControl(field: FormField): FormControl | FormArray {
    if (field.controlType === 'fieldgroup') {
      return this.createFieldGroupFormArray(field);
    }
    return this.createRegularFormControl(field);
  }

  private createFieldGroupFormArray(field: FormField): FormArray {
    const formArray = this.fb.array([]);

    const validators = [];
    if (field.fieldGroupConfig?.minInstances) {
      validators.push(Validators.minLength(field.fieldGroupConfig.minInstances));
    }
    if (
      field.fieldGroupConfig?.maxInstances &&
      field.fieldGroupConfig.maxInstances < Number.MAX_SAFE_INTEGER
    ) {
      validators.push(Validators.maxLength(field.fieldGroupConfig.maxInstances));
    }

    if (validators.length > 0) {
      formArray.setValidators(validators);
    }

    if (this.initialValues && field.formControlName in this.initialValues) {
      const initialData = this.initialValues[field.formControlName];

      if (Array.isArray(initialData) && initialData.length > 0) {
        initialData.forEach((itemData: any) => {
          const groupControls: { [key: string]: any } = {};

          field.fieldGroupConfig?.fields.forEach((groupField) => {
            const fieldValidators = this.buildValidators(groupField);
            const fieldValue = itemData[groupField.formControlName] ?? groupField.value ?? null;

            groupControls[groupField.formControlName] = [fieldValue, fieldValidators];
          });

          const formGroup: any = this.fb.group(groupControls);
          formArray.push(formGroup);
        });
      }
    }
    return formArray;
  }

  /**
   * Create regular form control with validators and initial value
   */
  private createRegularFormControl(field: FormField): FormControl {
    const validators = this.buildValidators(field);
    const initialValue = this.getInitialValue(field);

    const control = new FormControl(
      {
        value: initialValue,
        disabled: this.isFieldInitiallyDisabled(field),
      },
      {
        validators,
        updateOn: 'change',
      }
    );

    return control;
  }

  /**
   * Update validity for all form controls
   */
  private updateControlValidities(): void {
    setTimeout(() => {
      Object.keys(this.form.controls).forEach((key) => {
        const control = this.form.get(key);
        if (control) {
          control.updateValueAndValidity({ emitEvent: false });
        }
      });
    }, 0);
  }

  private getInitialValue(field: FormField): any {
    if (this.initialValues && field.formControlName in this.initialValues) {
      return this.initialValues[field.formControlName];
    }
    return field.value ?? null;
  }

  private isFieldInitiallyDisabled(field: FormField): boolean {
    if (this.disabled) return true;
    if (field.config?.disabled) return true;
    if (typeof field.disabled === 'boolean') return field.disabled;
    if (typeof field.disabled === 'function') {
      return field.disabled(this.initialValues ?? {});
    }
    return false;
  }

  private buildValidators(field: FormField): any[] {
    const validators: any[] = [];

    if (!field.validation) return validators;

    if (field.validation.required || field.config?.required) {
      validators.push(Validators.required);
    }
    if (field.validation.minLength) {
      validators.push(Validators.minLength(field.validation.minLength));
    }
    if (field.validation.maxLength) {
      validators.push(Validators.maxLength(field.validation.maxLength));
    }
    if (field.validation.min) {
      validators.push(Validators.min(field.validation.min));
    }
    if (field.validation.max) {
      validators.push(Validators.max(field.validation.max));
    }
    if (field.validation.pattern) {
      validators.push(Validators.pattern(field.validation.pattern));
    }
    if (field.validation.email) {
      validators.push(Validators.email);
    }
    if (field.validation.customValidators) {
      for (const customValidator of field.validation.customValidators) {
        validators.push((control: AbstractControl) => customValidator(control.value));
      }
    }

    return validators;
  }

  private setupFormSubscriptions(): void {
    this.form.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(50),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      )
      .subscribe((value) => {
        this.updateVisibility();
        this.updateDisabledState();

        // Handle dependent field data loading
        this.handleDependentFieldChanges(value);

        setTimeout(() => {
          this.emitFormState();
          this.cdr.markForCheck();
        }, 0);
      });
  }

  private updateVisibility(): void {
    const formValue = this.form.getRawValue();

    this.visibleSections = this.config.sections.filter((section) => {
      if (section.visible === undefined) return true;
      if (typeof section.visible === 'boolean') return section.visible;
      return section.visible(formValue);
    });

    this.config.sections.forEach((section) => {
      section.fields.forEach((field) => {
        let isVisible = true;

        if (field.visible !== undefined) {
          isVisible = typeof field.visible === 'boolean' ? field.visible : field.visible(formValue);
        }

        if (field.dependsOn && field.dependsOn.length > 0) {
          const dependencyMet = field.dependsOn.every((depFieldId) => {
            const depValue = formValue[depFieldId];
            return depValue !== null && depValue !== undefined && depValue !== '';
          });
          isVisible = isVisible && dependencyMet;
        }

        this.fieldVisibility.set(field.id, isVisible);
      });
    });
  }

  private updateDisabledState(): void {
    if (this.disabled) return;

    const formValue = this.form.getRawValue();

    this.config.sections.forEach((section) => {
      section.fields.forEach((field) => {
        const shouldDisable =
          typeof field.disabled === 'function'
            ? field.disabled(formValue)
            : field.disabled === true;

        this.fieldDisabled.set(field.id, shouldDisable);

        const control = this.form.get(field.formControlName);
        if (control) {
          if (shouldDisable && !control.disabled) {
            control.disable({ emitEvent: false });
          } else if (!shouldDisable && control.disabled) {
            control.enable({ emitEvent: false });
          }
        }
      });
    });
  }

  private emitFormState(): void {
    const state: DynamicFormState = {
      valid: this.form.valid,
      dirty: this.form.dirty,
      touched: this.form.touched,
      value: this.form.getRawValue(),
      errors: this.form.errors,
    };
    this.formChange.emit(state);
  }

  isFieldVisible(field: FormField): boolean {
    return this.fieldVisibility.get(field.id) ?? true;
  }

  isFieldDisabled(field: FormField): boolean {
    return this.fieldDisabled.get(field.id) ?? false;
  }

  onFieldValueChange(fieldId: string, value: any): void {
    this.fieldChange.emit({ fieldId, value });

    const field = this.findFieldById(fieldId);
    if (field && field.controlType === 'file') {
      const control = this.form.get(field.formControlName);
      if (control && control.value !== value) {
        control.setValue(value, { emitEvent: false });
        control.markAsDirty();
        control.markAsTouched();
      }
    }
  }

  onSubmit(): void {
    // Step 1: Mark all fields as touched and dirty
    this.validateAllFields();

    // Step 2: Trigger change detection in all child components
    this.triggerChildComponentChangeDetection();

    // Step 3: Force change detection
    this.cdr.detectChanges();

    // Step 4: Check if form has async validators
    if (this.hasAsyncValidators()) {
      // If form has async validators, wait for them to complete
      this.form.statusChanges
        .pipe(
          filter((status) => status !== 'PENDING'),
          take(1),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.processFormSubmission();
        });
    } else {
      // No async validators, submit immediately
      this.processFormSubmission();
    }
  }

  onCancel(): void {
    this.formCancel.emit();
  }

  private processFormSubmission(): void {
    // Final validation check
    if (this.config.validateOnSubmit !== false) {
      if (this.form.invalid) {
        console.warn('Form validation failed:', this.getValidationErrors());
        this.focusFirstInvalidField();
        return;
      }
    }

    // Form is valid, proceed with submission
    const formValue = this.form.getRawValue();
    this.formSubmit.emit(formValue);

    if (this.config.resetOnSubmit) {
      this.form.reset();
      this.updateVisibility();
      this.updateDisabledState();
    }
  }

  private hasAsyncValidators(): boolean {
    const controls = this.form.controls;
    for (const key in controls) {
      if (controls.hasOwnProperty(key)) {
        const control = controls[key];
        if (control.status === 'PENDING') {
          return true;
        }
        // Check nested FormGroup or FormArray
        if (control instanceof FormGroup || control instanceof FormArray) {
          if (this.hasAsyncValidatorsInControl(control)) {
            return true;
          }
        }
      }
    }
    return false;
  }

  private hasAsyncValidatorsInControl(control: FormGroup | FormArray): boolean {
    if (control.status === 'PENDING') {
      return true;
    }

    if (control instanceof FormGroup) {
      const controls = control.controls;
      for (const key in controls) {
        if (controls.hasOwnProperty(key)) {
          const childControl = controls[key];
          if (childControl.status === 'PENDING') {
            return true;
          }
          if (childControl instanceof FormGroup || childControl instanceof FormArray) {
            if (this.hasAsyncValidatorsInControl(childControl)) {
              return true;
            }
          }
        }
      }
    } else if (control instanceof FormArray) {
      for (const childControl of control.controls) {
        if (childControl.status === 'PENDING') {
          return true;
        }
        if (childControl instanceof FormGroup || childControl instanceof FormArray) {
          if (this.hasAsyncValidatorsInControl(childControl)) {
            return true;
          }
        }
      }
    }

    return false;
  }

  private triggerChildComponentChangeDetection(): void {
    this.inputComponents?.forEach((component) => {
      component['cdr'].markForCheck();
    });

    this.selectComponents?.forEach((component) => {
      component['cdr']?.markForCheck();
    });

    this.textareaComponents?.forEach((component) => {
      component['cdr'].markForCheck();
    });

    this.datepickerComponents?.forEach((component) => {
      component['cdr']?.markForCheck();
    });

    this.timepickerComponents?.forEach((component) => {
      component['cdr']?.markForCheck();
    });

    this.colorpickerComponents?.forEach((component) => {
      component['cdr']?.markForCheck();
    });

    this.checkboxComponents?.forEach((component) => {
      component['cdr'].markForCheck();
    });

    this.radioComponents?.forEach((component) => {
      component['cdr'].markForCheck();
    });

    this.fileUploadComponents?.forEach((component) => {
      component['cdr']?.markForCheck();
    });

    this.fieldGroupComponents?.forEach((component) => {
      component['cdr']?.markForCheck();
    });
  }

  private markFormGroupTouched(formGroup: FormGroup | AbstractControl): void {
    if (formGroup instanceof FormGroup) {
      Object.keys(formGroup.controls).forEach((key) => {
        const control = formGroup.get(key);
        if (control) {
          control.markAsTouched({ onlySelf: false });
          control.markAsDirty({ onlySelf: false });

          control.updateValueAndValidity({ emitEvent: true });

          if (control instanceof FormGroup) {
            this.markFormGroupTouched(control);
          }
          if (control instanceof FormArray) {
            control.controls.forEach((arrayControl) => {
              this.markFormGroupTouched(arrayControl);
            });
          }
        }
      });
    } else {
      formGroup.markAsTouched({ onlySelf: false });
      formGroup.markAsDirty({ onlySelf: false });
      formGroup.updateValueAndValidity({ emitEvent: true });
    }
  }

  validateAllFields(): void {
    this.markFormGroupTouched(this.form);

    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      if (control) {
        control.markAsTouched({ onlySelf: false });
        control.markAsDirty({ onlySelf: false });
        control.updateValueAndValidity({ onlySelf: false, emitEvent: true });

        // Force update for FormArray controls
        if (control.constructor.name === 'FormArray') {
          control.updateValueAndValidity({ emitEvent: true });
        }
      }
    });

    this.emitFormState();
    this.cdr.markForCheck();
  }

  private focusFirstInvalidField(): void {
    const firstInvalidControl = this.getFirstInvalidControl();

    if (firstInvalidControl) {
      setTimeout(() => {
        const invalidElement = document.querySelector(
          `[formcontrolname="${firstInvalidControl}"]`
        ) as HTMLElement;

        if (invalidElement) {
          invalidElement.focus();
          invalidElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 0);
    }
  }

  private getFirstInvalidControl(): string | null {
    const controls = this.form.controls;

    for (const section of this.config.sections) {
      for (const field of section.fields) {
        const control = controls[field.formControlName];
        if (control && control.invalid && this.isFieldVisible(field)) {
          return field.formControlName;
        }
      }
    }

    return null;
  }

  getFieldControl(field: FormField): FormControl {
    return this.form.get(field.formControlName) as FormControl;
  }

  getGridClass(field: FormField): string {
    if (field.controlType === 'fieldgroup') {
      return 'col-12'; // Full width for fieldgroup
    }

    if (!field.gridPosition) return 'col-12';

    const colSpan = field.gridPosition.colSpan ?? 12;
    const customClass = field.gridPosition.colClass ?? '';

    return `col-md-${colSpan} ${customClass}`.trim();
  }

  getSectionLayoutClass(section: FormSection): string {
    if (!section.layout) return 'row';

    switch (section.layout.type) {
      case 'grid':
        return 'row';
      case 'flex':
        return 'd-flex flex-wrap';
      case 'vertical':
        return 'd-flex flex-column';
      default:
        return 'row';
    }
  }

  getSectionGapStyle(section: FormSection): any {
    if (!section.layout?.gap) return {};

    return {
      gap: section.layout.gap,
      'column-gap': section.layout.columnGap ?? section.layout.gap,
      'row-gap': section.layout.rowGap ?? section.layout.gap,
    };
  }

  get showActions(): boolean {
    if (this.currentMode === 'popup') return false;
    return this.config?.showActions !== false;
  }

  reset(): void {
    this.form.reset(this.initialValues);
    this.previousFormValues = {};
    this.dataSourcesInitialized = false;
    this.updateVisibility();
    this.updateDisabledState();
    this.initializeDataSources();
    this.cdr.markForCheck();
  }

  patchValues(values: Partial<DynamicFormValue>): void {
    this.form.patchValue(values);
    this.updateVisibility();
    this.updateDisabledState();
    this.cdr.markForCheck();
  }

  setValues(values: DynamicFormValue): void {
    this.form.setValue(values);
    this.updateVisibility();
    this.updateDisabledState();
    this.cdr.markForCheck();
  }

  getValue(): DynamicFormValue {
    return this.form.getRawValue();
  }

  getFieldValue(fieldName: string): any {
    return this.form.get(fieldName)?.value;
  }

  setFieldValue(fieldName: string, value: any): void {
    const control = this.form.get(fieldName);
    if (control) {
      control.setValue(value);
      control.markAsDirty();
      control.markAsTouched();
    }
  }

  /**
   * Update field data dynamically (useful for external data updates)
   */
  updateFieldData(fieldId: string, data: any[]): void {
    const field = this.findFieldById(fieldId);
    if (field) {
      this.updateFieldDataSource(field, data);
    }
  }

  /**
   * Reload data for a specific field
   */
  reloadFieldData(fieldId: string): void {
    const field = this.findFieldById(fieldId);
    if (field?.dataSource) {
      const formValue = this.form.getRawValue();
      this.loadFieldData(field, formValue);
    }
  }

  isValid(): boolean {
    return this.form.valid;
  }

  isDirty(): boolean {
    return this.form.dirty;
  }

  isTouched(): boolean {
    return this.form.touched;
  }

  markAsDirty(): void {
    this.form.markAsDirty();
    Object.keys(this.form.controls).forEach((key) => {
      this.form.get(key)?.markAsDirty();
    });
  }

  markAsTouched(): void {
    this.form.markAsTouched();
    Object.keys(this.form.controls).forEach((key) => {
      this.form.get(key)?.markAsTouched();
    });
  }

  getErrors(): any {
    return this.form.errors;
  }

  getFieldErrors(fieldName: string): any {
    return this.form.get(fieldName)?.errors;
  }

  getValidationErrors(): { [key: string]: any } {
    const errors: { [key: string]: any } = {};

    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      if (control && control.errors) {
        errors[key] = control.errors;
      }
    });

    return errors;
  }

  hasFieldError(fieldName: string): boolean {
    const control = this.form.get(fieldName);
    return !!(control?.errors && (control.dirty ?? control.touched));
  }

  enableField(fieldName: string): void {
    this.form.get(fieldName)?.enable();
  }

  disableField(fieldName: string): void {
    this.form.get(fieldName)?.disable();
  }

  getFormGroup(): FormGroup {
    return this.form;
  }

  private findFieldById(fieldId: string): FormField | undefined {
    for (const section of this.config.sections) {
      const field = section.fields.find((f) => f.id === fieldId);
      if (field) return field;
    }
    return undefined;
  }

  /**
   * Get the config for select fields, merging addNewConfig if present
   */
  getSelectConfig(field: FormField): any {
    if (!field.addNewConfig) {
      return field.config;
    }

    return {
      ...field.config,
      addNewConfig: field.addNewConfig,
    };
  }
}
